import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../common/Modal';
import { Switch } from '../common/Switch';
import { Quotation } from '../../types/quotation';
import { InventoryMovementType, InventoryReport } from '../../types/warehouse';
import { useAuth } from '../../contexts/AuthContext';
import { useProducts } from '../../hooks/useProducts';
import { useWarehouses } from '../../hooks/useWarehouses';
import { useInventory } from '../../hooks/useInventory';
import { useTasks } from '../../hooks/useTasks';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useCustomers } from '../../hooks/useCustomers';
import { DocumentChainService } from '../../services/documentChainService';
import { customerDisplayName } from '../../utils/customerName';
import { fmtMoney, quotationTotal } from './quotationStatus';
import { ShoppingCartIcon, CubeIcon, WrenchScrewdriverIcon, ExclamationTriangleIcon, CheckCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  quotation: Quotation | null;
  onCreated?: (orderId: string) => void;
}

type Kind = 'product' | 'service' | 'unknown';

interface Row {
  idx: number;
  name: string;
  code?: string;
  qty: number;
  kind: Kind;
  productId: string;
  available: number;            // összes elérhető (minden raktár)
  warehouseId: string;          // választott raktár
  warehouses: { id: string; name: string; available: number }[];
  reserve: boolean;             // terméknél: foglalás
  task: boolean;                // szolgáltatásnál: feladat
}

const addr = (a?: { street?: string; city?: string; postalCode?: string; country?: string } | null) =>
  a ? [[a.postalCode, a.city].filter(Boolean).join(' '), a.street].filter(Boolean).join(', ') : '';

const QuotationToOrderModal: React.FC<Props> = ({ isOpen, onClose, quotation, onCreated }) => {
  const { user } = useAuth();
  const { products } = useProducts();
  const { warehouses } = useWarehouses();
  const { getInventoryReport, createInventoryMovement } = useInventory();
  const { createTask } = useTasks();
  const { organizations } = useOrganizations();
  const { customers } = useCustomers();

  const [rows, setRows] = useState<Row[]>([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const organization = useMemo(() => organizations?.find(o => o.id === quotation?.organizationId), [organizations, quotation]);
  const customer = useMemo(() => customers?.find(c => c.id === quotation?.customerId), [customers, quotation]);

  // Sorok felépítése + készlet lekérése
  useEffect(() => {
    if (!isOpen || !quotation) return;
    let cancelled = false;
    const build = async () => {
      setLoadingStock(true);
      setNotes(quotation.notes || '');
      setDeliveryDate('');
      const items = quotation.items || [];
      const base: Row[] = items.map((it, idx) => {
        const p = products.find(x => x.id === it.productId);
        const kind: Kind = !p ? 'unknown' : (p.type === 'service' || p.isService) ? 'service' : 'product';
        return {
          idx, name: it.productName, code: it.productCode, qty: Number(it.quantity) || 1, kind,
          productId: it.productId || '', available: 0, warehouseId: '', warehouses: [],
          reserve: kind === 'product', task: kind === 'service'
        };
      });
      const productIds = base.filter(r => r.kind === 'product' && r.productId).map(r => r.productId);
      let report: InventoryReport[] = [];
      if (productIds.length) {
        try { report = await getInventoryReport(productIds); } catch (e) { console.error(e); }
      }
      if (cancelled) return;
      setRows(base.map(r => {
        if (r.kind !== 'product') return r;
        const rep = report.find(x => x.productId === r.productId);
        const whs = (rep?.warehouseDetails || []).map(w => ({ id: w.warehouseId, name: w.warehouseName, available: w.availableQuantity }));
        const allWhs = warehouses.map(w => ({ id: w.id, name: w.name, available: whs.find(x => x.id === w.id)?.available || 0 }));
        const best = [...allWhs].sort((a, b) => b.available - a.available)[0];
        return { ...r, available: rep?.availableQuantity || 0, warehouses: allWhs, warehouseId: best?.id || '', reserve: r.reserve && !!best && (best.available >= r.qty) };
      }));
      setLoadingStock(false);
    };
    build();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, quotation?.id, products.length, warehouses.length]);

  if (!quotation) return null;

  const update = (idx: number, patch: Partial<Row>) => setRows(rs => rs.map(r => (r.idx === idx ? { ...r, ...patch } : r)));

  const productRows = rows.filter(r => r.kind === 'product');
  const serviceRows = rows.filter(r => r.kind === 'service');
  const shortage = productRows.filter(r => r.reserve && (r.warehouses.find(w => w.id === r.warehouseId)?.available || 0) < r.qty);

  const handleCreate = async () => {
    if (!user) return;
    try {
      setBusy(true);
      const orgAddr = addr(organization?.address);
      const custAddr = customer ? addr((customer as any).address) : '';
      const order = await DocumentChainService.createOrderFromQuotation(quotation, quotation.organizationId, (user as any).id || (user as any).uid, {
        status: 'confirmed',
        customerName: customer ? customerDisplayName(customer) : (quotation.customerName || quotation.companyName || ''),
        customerAddress: custAddr || quotation.companyAddress || '',
        customerTaxNumber: (customer as any)?.taxNumber || quotation.taxNumber || '',
        customerEmail: (customer as any)?.email || '',
        customerPhone: (customer as any)?.phone || '',
        contactName: quotation.contactName || '',
        organizationName: organization?.name || '',
        organizationAddress: orgAddr,
        organizationTaxNumber: organization?.taxNumber || '',
        organizationRegistrationNumber: (organization as any)?.registrationNumber || '',
        representativeName: quotation.representativeName || '',
        requestedDeliveryDate: deliveryDate || undefined,
        deliveryAddress: custAddr || undefined,
        subtotal: Number(quotation.subtotal) || 0,
        taxAmount: Number(quotation.taxAmount) || 0,
        totalAmount: quotationTotal(quotation),
        currency: quotation.currency || 'HUF',
        paymentTerms: quotation.paymentDue ? `${quotation.paymentDue} nap` : undefined,
        notes
      });

      // Készletfoglalás termékekre
      let reserved = 0, failed = 0;
      for (const r of productRows.filter(x => x.reserve && x.warehouseId)) {
        try {
          await createInventoryMovement({
            productId: r.productId, warehouseId: r.warehouseId, quantity: r.qty,
            type: InventoryMovementType.STOCK_RESERVATION,
            documentId: order.id, documentType: 'order', documentNumber: order.number,
            note: `Foglalás – megrendelés ${order.number} (árajánlat ${quotation.quotationNumber || quotation.number})`
          });
          reserved++;
        } catch (e) { console.error('reservation failed', e); failed++; }
      }

      // Feladat szolgáltatásokra
      let tasks = 0;
      for (const r of serviceRows.filter(x => x.task)) {
        try {
          await createTask({
            title: `${r.name} – ${order.customerName || 'ügyfél'}`,
            description: `Megrendelés: ${order.number}\nÁrajánlat: ${quotation.quotationNumber || quotation.number}\nMennyiség: ${r.qty}${notes ? `\n\n${notes}` : ''}`,
            priority: 'medium', status: 'todo',
            customerId: quotation.customerId || undefined,
            dueDate: deliveryDate ? new Date(deliveryDate) : undefined,
            tags: ['megrendelés', 'szolgáltatás'], documents: []
          });
          tasks++;
        } catch (e) { console.error('task failed', e); }
      }

      const parts = [`Megrendelés létrehozva: ${order.number}`];
      if (reserved) parts.push(`${reserved} tétel lefoglalva`);
      if (tasks) parts.push(`${tasks} feladat`);
      toast.success(parts.join(' · '));
      if (failed) toast.error(`${failed} tétel foglalása nem sikerült – ellenőrizd a raktárban`);
      onCreated?.(order.id);
      onClose();
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'A megrendelés létrehozása nem sikerült');
    } finally {
      setBusy(false);
    }
  };

  const KindBadge: React.FC<{ kind: Kind }> = ({ kind }) => (
    kind === 'product' ? <span className="inline-flex items-center gap-1 text-[11px] text-sky-700 dark:text-sky-300"><CubeIcon className="w-3.5 h-3.5" />Termék</span>
      : kind === 'service' ? <span className="inline-flex items-center gap-1 text-[11px] text-violet-700 dark:text-violet-300"><WrenchScrewdriverIcon className="w-3.5 h-3.5" />Szolgáltatás</span>
        : <span className="inline-flex items-center gap-1 text-[11px] text-gray-500"><QuestionMarkCircleIcon className="w-3.5 h-3.5" />Egyedi tétel</span>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Megrendelés készítése az árajánlatból" size="3xl" noPadding>
      <div className="flex flex-col min-h-full">
        <div className="px-4 sm:px-6 pt-4 pb-3 border-b border-[var(--crm-border)] flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-50">{quotation.quotationNumber || quotation.number}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{customer ? customerDisplayName(customer) : '–'}{organization ? ` · ${organization.name}` : ''}</div>
          </div>
          <div className="text-sm font-semibold tabular-nums whitespace-nowrap">{fmtMoney(quotationTotal(quotation), quotation.currency || 'HUF')}</div>
        </div>

        <div className="flex-1 p-4 sm:p-6 space-y-4">
          {quotation.status !== 'accepted' && quotation.status !== 'success' && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              Az árajánlat még nincs elfogadva. Megrendelést így is készíthetsz (pl. szóbeli elfogadásnál), az ajánlat státusza nem változik.
            </div>
          )}

          <div className="crm-card overflow-hidden">
            <div className="px-3 py-2 border-b border-[var(--crm-border)] text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center justify-between">
              <span>Tételek ({rows.length})</span>
              {loadingStock && <span className="normal-case font-normal">Készlet betöltése…</span>}
            </div>
            <ul className="divide-y divide-[var(--crm-border)]">
              {rows.map(r => {
                const wh = r.warehouses.find(w => w.id === r.warehouseId);
                const short = r.kind === 'product' && r.reserve && (wh?.available || 0) < r.qty;
                return (
                  <li key={r.idx} className="px-3 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">{r.name}</div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                        <KindBadge kind={r.kind} />
                        <span className="tabular-nums">Mennyiség: {r.qty}</span>
                        {r.code && <span className="font-mono">{r.code}</span>}
                        {r.kind === 'product' && !loadingStock && (
                          <span className={`tabular-nums ${short ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                            Elérhető{wh ? ` (${wh.name})` : ''}: {wh?.available ?? r.available}
                          </span>
                        )}
                      </div>
                    </div>
                    {r.kind === 'product' && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <select value={r.warehouseId} onChange={e => update(r.idx, { warehouseId: e.target.value })} className="h-8 pl-2 pr-7 text-xs border rounded-lg max-w-[170px]" disabled={!r.warehouses.length}>
                          {r.warehouses.length === 0 && <option value="">Nincs raktár</option>}
                          {r.warehouses.map(w => <option key={w.id} value={w.id}>{w.name} ({w.available})</option>)}
                        </select>
                        <label className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 cursor-pointer whitespace-nowrap">
                          <Switch checked={r.reserve} onChange={(v) => update(r.idx, { reserve: v })} size="sm" />Foglalás
                        </label>
                      </div>
                    )}
                    {r.kind === 'service' && (
                      <label className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 cursor-pointer whitespace-nowrap flex-shrink-0">
                        <Switch checked={r.task} onChange={(v) => update(r.idx, { task: v })} size="sm" />Feladat létrehozása
                      </label>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {shortage.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>{shortage.length} tételből nincs elég szabad készlet a választott raktárban ({shortage.map(s => s.name).join(', ')}) – ezeknél a foglalás nem fog sikerülni. Válassz másik raktárt, kapcsold ki a foglalást, vagy előbb vételezd be / rendeld meg a beszállítótól.</div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Kért teljesítési / szállítási dátum</label>
              <input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} className="w-full h-9 border rounded-lg px-3 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Megjegyzés a megrendeléshez</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1.5">
            <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
            <span>A megrendelés „Visszaigazolt” státuszban jön létre, az árajánlathoz kapcsolva. Termékeknél a foglalás nem von le készletet – az a szállítólevélnél történik. Szolgáltatásoknál feladat nyílik a teljesítéshez.</span>
          </div>
        </div>

        <div className="mt-auto sticky bottom-0 z-10 border-t border-[var(--crm-border)] bg-[var(--crm-surface)] px-4 sm:px-6 py-3 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))] flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="crm-btn crm-btn-ghost mr-auto" disabled={busy}>Mégse</button>
          <button type="button" onClick={handleCreate} disabled={busy || loadingStock || rows.length === 0} className="crm-btn crm-btn-primary">
            <ShoppingCartIcon className="w-4 h-4 mr-1.5" />{busy ? 'Létrehozás…' : 'Megrendelés létrehozása'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default QuotationToOrderModal;
